import { createSelector } from 'reselect';
import { useAppSelector } from 'store/hooks';
import { assertUnreachable } from 'utils/types';
import { getBalance } from 'utils/api/getBalance';
import { selectGBAccountsWithPISPInfo } from 'reducers/selectors';
import { selectSelectedSpace } from 'features/spaces/reducers/selectors';
import { activitySelector, selectAssetsForWatchListFilter, selectPendingAndQueuedOrders, selectTradingAccount, selectStockNewsForAssetId, selectActiveTradingAccountId, } from '../reducers/selectors';
import * as debug from '../debug';
import { SelectedPositionFilter } from '../types';
import { filterPortfolioByGainLoss, filterPortfolioByTotalValue, filterPortfolioByReturnPercentage, } from '../utils/format';
export { default as useStockMeta } from './useStockMeta';
export const useStatus = () => useAppSelector(selectTradingAccount)?.account.status;
export const useRequiredUserAction = () => {
    const account = useAppSelector(selectTradingAccount);
    return account?.account.requiredUserAction;
};
export const useConnectedAccount = () => useAppSelector((state) => debug.emulateNoConnectedAccount ? null : state.invest.connectedAccount);
export const selectConnectedAccountWithBankId = createSelector([(state) => state.invest.connectedAccount], (connectedAccount) => {
    // connected account is undefined if the user just opened the trading account but hasnt topped up emma wallet or GIA account
    // in this case, we let them select from the list of OB accounts if possible
    if (!connectedAccount) {
        return {
            connectedAccount: undefined,
        };
    }
    // if the user has connectedAccount but no bankId, we force them to use the Emma Wallet
    if (!connectedAccount?.bankId) {
        return {
            connectedAccount: undefined,
        };
    }
    return {
        connectedAccount: connectedAccount,
    };
});
export const selectConnectedAccountBalanceFromConnectedAccountWithBankId = createSelector([selectConnectedAccountWithBankId, selectGBAccountsWithPISPInfo], ({ connectedAccount }, { accounts }) => {
    if (connectedAccount?.accountId && accounts?.length) {
        const account = accounts.find(({ id }) => id === connectedAccount.accountId);
        if (account) {
            const [balance] = getBalance(account);
            return balance || undefined;
        }
    }
    return undefined;
});
export const usePosition = (assetId) => {
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const positions = useAppSelector((state) => assetId && tradingAccountId ? state.invest.tradingAccountPositionsCache?.[tradingAccountId]?.[assetId] : undefined);
    return positions;
};
const ordersSelector = createSelector((state, { assetId, status, tradingAccountId, }) => {
    if (tradingAccountId) {
        if (assetId) {
            // showing queued orders only by default
            if (status === 'queued' || status === undefined) {
                return state.invest.orderIdForAssetIdCache[assetId];
            }
            if (status === 'pending') {
                return state.invest.pendingOrderIdForAssetIdCache[assetId];
            }
            if (status === 'limitAndStop') {
                return state.invest.limitStopOrderIdForAssetIdCache?.[assetId];
            }
            assertUnreachable(status);
        }
        if (status === 'queued' || status === undefined) {
            return state.invest.queuedOrdersForTradingAccountCache[tradingAccountId];
        }
        if (status === 'pending') {
            return state.invest.pendingOrdersForTradingAccountCache[tradingAccountId];
        }
        if (status === 'limitAndStop') {
            return state.invest.limitStopOrderIdCache;
        }
        assertUnreachable(status);
    }
    return [];
}, (state) => state.invest.ordersCache, (orderIdsForAssetIdCache, ordersCache) => {
    if (!orderIdsForAssetIdCache) {
        return undefined;
    }
    const result = [];
    orderIdsForAssetIdCache.forEach((orderId) => {
        const order = ordersCache[orderId];
        if (order) {
            result.push(order);
        }
    });
    return result;
});
export const useOrders = (assetId, status, tradingAccountId) => useAppSelector((state) => ordersSelector(state, { assetId, status, tradingAccountId }));
export const usePendingAndQueuedOrders = () => useAppSelector(selectPendingAndQueuedOrders);
export const useActivity = (assetId) => useAppSelector((state) => activitySelector(state, assetId));
const positionsSelectorSorted = createSelector([
    (state) => state.invest.tradingAccountPositionsCache,
    (_state, selectedPositionFilter) => selectedPositionFilter,
    selectActiveTradingAccountId,
], (accountIdPositionsCache, selectedPositionFilter, tradingAccountId) => {
    const result = [];
    if (tradingAccountId) {
        const positionsCache = accountIdPositionsCache?.[tradingAccountId] || {};
        const assetIds = Object.keys(positionsCache);
        switch (selectedPositionFilter) {
            case SelectedPositionFilter.TOTAL_VALUE:
                filterPortfolioByTotalValue(assetIds, positionsCache);
                break;
            case SelectedPositionFilter.GAIN_LOSS:
                filterPortfolioByGainLoss(assetIds, positionsCache);
                break;
            case SelectedPositionFilter.RETURN_PERCENT:
                filterPortfolioByReturnPercentage(assetIds, positionsCache);
                break;
            default:
                break;
        }
        assetIds.forEach((assetId) => {
            const position = positionsCache[assetId];
            // the following condition will always return true since we are looping over keys of positionsCache
            if (position) {
                result.push(position);
            }
        });
    }
    return result;
});
export const usePositions = (selectedPositionFilter) => useAppSelector((state) => positionsSelectorSorted(state, selectedPositionFilter));
// The positions cache entry for an account is only created once GET_POSITIONS_SUCCESS has been received
export const selectPositionsLoaded = createSelector([(state) => state.invest.tradingAccountPositionsCache, selectActiveTradingAccountId], (tradingAccountPositionsCache, tradingAccountId) => !!tradingAccountId && tradingAccountPositionsCache?.[tradingAccountId] !== undefined);
export const selectPositionsLength = createSelector([(state) => state.invest.tradingAccountPositionsCache, selectActiveTradingAccountId], (tradingAccountPositionsCache, tradingAccountId) => {
    if (tradingAccountId) {
        const positionsCache = tradingAccountPositionsCache?.[tradingAccountId];
        if (positionsCache) {
            return Object.keys(positionsCache).length;
        }
    }
    return 0;
});
export const selectCanSellForTradingAccount = createSelector([(state) => state.invest.tradingAccountPositionsCache, selectActiveTradingAccountId], (tradingAccountPositionsCache, tradingAccountId) => {
    const positionsCache = tradingAccountId ? tradingAccountPositionsCache?.[tradingAccountId] : undefined;
    if (positionsCache) {
        return Object.values(positionsCache).some((positionCache) => positionCache && positionCache?.availableToSell > 0);
    }
    return false;
});
export const selectHasPositionsOrOrders = createSelector((state) => state.invest.tradingAccountPositionsCache, selectPendingAndQueuedOrders, selectActiveTradingAccountId, (tradingAccountPositionsCache, { queuedOrders, pendingOrders }, tradingAccountId) => {
    const positionsCache = tradingAccountId ? tradingAccountPositionsCache?.[tradingAccountId] : undefined;
    if (queuedOrders.length ||
        pendingOrders.length ||
        (positionsCache && Object.values(positionsCache).filter((positionCache) => positionCache !== undefined).length)) {
        return true;
    }
    return false;
});
/**
 * useless to memo this because the values keeps changing
 */
export const useCurrentPrice = (assetId) => useAppSelector((state) => (assetId ? state.invest.currentPriceCaches[assetId] : undefined));
export const useOnboardingState = () => useAppSelector((state) => state.invest.onboarding);
export const selectTrustedContacts = (state) => state.invest.trustedContacts;
/**
 * Plain selector hook that extracts price change from `invest.priceChangeCache`.
 * @param symbol The symbol you want price change for
 * @returns Cached 1D price change from snapshot or assets endpoint
 */
export const usePriceChange = (assetId) => useAppSelector((state) => assetId ? state.invest.priceChangeCaches?.[assetId] : undefined);
export const useWatchlistCache = () => useAppSelector((state) => state.invest.watchlists) || [];
export const useWatchListAssetsCache = () => useAppSelector((state) => state.watchList.watchListAssetCache) ||
    {};
export const useWatchListAssetsCacheForWatchList = (watchListId, selectedFilter) => useAppSelector((state) => selectAssetsForWatchListFilter(state, watchListId, selectedFilter)) || [];
export const useIsAssetIdInWatchLists = (assetId) => {
    const watchLists = useAppSelector((state) => assetId ? state.watchList.stockWatchListsInfoMap?.[assetId] : undefined);
    if (!watchLists)
        return undefined;
    const keys = Object.keys(watchLists);
    return !!keys?.length;
};
export const useWatchListStockInfoMap = (watchListId, assetId) => useAppSelector((state) => state.watchList.stockWatchListsInfoMap?.[assetId]?.[watchListId]) ||
    false;
export const useGetWatchListInfoForWatchListId = (id) => {
    const watchLists = useAppSelector((state) => state.invest.watchlists);
    if (!watchLists)
        return undefined;
    const watchList = watchLists.find((watchlist) => watchlist.id === id);
    if (watchList) {
        return { id: watchList.id, name: watchList?.name, emoji: watchList.emoji };
    }
    return undefined;
};
/**
 * Selects news for a given assetId.
 *
 * Guarantees news to be unique by `id` property.
 */
export const useStockNews = (assetId, totalNumber) => useAppSelector((state) => selectStockNewsForAssetId(state, assetId, totalNumber));
export const selectPortfolioBalance = createSelector(selectSelectedSpace, (state) => state.invest.spaceTradingAccounts, (spaceId, spaceTradingAccounts) => {
    const defaultValue = {
        value: 0,
        currency: 'GBP',
    };
    if (!spaceId)
        return defaultValue;
    const tradingAccounts = spaceTradingAccounts?.[spaceId] || [];
    if (!tradingAccounts.length)
        return defaultValue;
    return {
        value: tradingAccounts.reduce((prev, curr) => prev + (curr?.balance?.value || 0), 0),
        currency: 'GBP',
    };
});
export const selectHasMultiplePositions = createSelector([(state) => state.invest.tradingAccountPositionsCache, selectActiveTradingAccountId], (positionsCache, tradingAccountId) => {
    if (!tradingAccountId)
        return false;
    const positions = positionsCache?.[tradingAccountId];
    if (!positions)
        return false;
    const positionsCount = Object.values(positions).filter((position) => !!position).length;
    return positionsCount > 1;
});
